import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Plus, 
  Edit, 
  Trash2, 
  Copy,
  Star,
  FileText,
  Settings
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { CertificateEditor } from "./CertificateEditor";

interface CertificateTemplate {
  id: string;
  name: string;
  description: string;
  canvas_data: any;
  preview_url?: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
  background_image?: string;
  module_id?: string;
}

interface TrainingModule {
  id: string;
  title_en: string;
  title_el: string;
  certificate_template_id?: string;
}

interface ModuleTemplateAssignment {
  module_id: string;
  template_id: string;
}

export const CertificateTemplatesManager = () => {
  const { language } = useLanguage();
  const [templates, setTemplates] = useState<CertificateTemplate[]>([]);
  const [modules, setModules] = useState<TrainingModule[]>([]);
  const [moduleAssignments, setModuleAssignments] = useState<ModuleTemplateAssignment[]>([]);
  const [editingTemplate, setEditingTemplate] = useState<CertificateTemplate | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isAssignmentDialogOpen, setIsAssignmentDialogOpen] = useState(false);
  const [selectedModuleId, setSelectedModuleId] = useState<string>('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      // Check if super admin
      const { data: isSuperAdminData } = await supabase.rpc('is_super_admin', { user_id: user.id });
      const isSuperAdmin = isSuperAdminData === true;
      
      // Get admin bubble IDs
      let bubbleAdminIds = [user.id];
      if (!isSuperAdmin) {
        const { data: contributors } = await supabase
          .from('admin_contributors')
          .select('contributor_admin_id')
          .eq('parent_admin_id', user.id);
        
        bubbleAdminIds = [user.id, ...(contributors?.map(c => c.contributor_admin_id) || [])];
      }
      
      // Fetch templates from database
      let templatesQuery = supabase
        .from('certificate_templates')
        .select('*')
        .order('is_default', { ascending: false })
        .order('created_at', { ascending: false });
      
      // Filter by bubble if not super admin
      if (!isSuperAdmin) {
        templatesQuery = templatesQuery.in('created_by', bubbleAdminIds);
      }
      
      const { data: templatesData, error: templatesError } = await templatesQuery;

      if (templatesError) {
        console.error('Error fetching templates:', templatesError);
        setTemplates([]);
      } else {
        setTemplates(templatesData || []);
      }

      // Fetch modules (only from admin's bubble)
      let modulesQuery = supabase
        .from('training_modules')
        .select('id, title_en, title_el, certificate_template_id')
        .eq('is_active', true)
        .order('title_en');
      
      // Filter by bubble if not super admin
      if (!isSuperAdmin) {
        modulesQuery = modulesQuery.in('created_by', bubbleAdminIds);
      }
      
      const { data: modulesData, error: modulesError } = await modulesQuery;

      if (modulesError) {
        console.error('Error fetching modules:', modulesError);
      } else {
        setModules(modulesData || []);
        
        // Create module assignments array
        const assignments: ModuleTemplateAssignment[] = (modulesData || [])
          .filter(module => module.certificate_template_id)
          .map(module => ({
            module_id: module.id,
            template_id: module.certificate_template_id!
          }));
        setModuleAssignments(assignments);
      }

    } catch (error) {
      console.error('Error fetching templates data:', error);
      toast.error('Failed to load certificate templates');
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async (templateData: Partial<CertificateTemplate>) => {
    try {
      if (editingTemplate?.id) {
        // Update existing template
        const { error } = await supabase
          .from('certificate_templates')
          .update({
            name: templateData.name,
            description: templateData.description,
            canvas_data: templateData.canvas_data,
            module_id: templateData.module_id || null,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingTemplate.id);

        if (error) throw error;
        toast.success('Template updated successfully!');
      } else {
        // Create new template
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        const { error } = await supabase
          .from('certificate_templates')
          .insert({
            name: templateData.name,
            description: templateData.description,
            canvas_data: templateData.canvas_data || {},
            module_id: templateData.module_id || null,
            is_default: templateData.is_default || false,
            created_by: user.id
          });

        if (error) throw error;
        toast.success('Template created successfully!');
      }

      setIsEditorOpen(false);
      setEditingTemplate(null);
      fetchData();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    }
  };

  const deleteTemplate = async (templateId: string) => {
    try {
      // Check if template is assigned to any modules
      const assignedModules = moduleAssignments.filter(a => a.template_id === templateId);
      if (assignedModules.length > 0) {
        toast.error('Cannot delete template that is assigned to modules. Please unassign it first.');
        return;
      }

      const { error } = await supabase
        .from('certificate_templates')
        .delete()
        .eq('id', templateId);

      if (error) throw error;
      toast.success('Template deleted successfully!');
      fetchData();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const duplicateTemplate = async (template: CertificateTemplate) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('certificate_templates')
        .insert({
          name: `${template.name} (Copy)`,
          description: template.description,
          canvas_data: template.canvas_data,
          is_default: false,
          created_by: user.id
        });

      if (error) throw error;
      toast.success('Template duplicated successfully!');
      fetchData();
    } catch (error) {
      console.error('Error duplicating template:', error);
      toast.error('Failed to duplicate template');
    }
  };

  const assignTemplateToModule = async () => {
    if (!selectedModuleId || !selectedTemplateId) return;

    try {
      const { error } = await supabase
        .from('training_modules')
        .update({ certificate_template_id: selectedTemplateId })
        .eq('id', selectedModuleId);

      if (error) throw error;
      toast.success('Template assigned to module successfully!');
      setIsAssignmentDialogOpen(false);
      setSelectedModuleId('');
      setSelectedTemplateId('');
      fetchData();
    } catch (error) {
      console.error('Error assigning template:', error);
      toast.error('Failed to assign template');
    }
  };

  const addNewTemplate = () => {
    setEditingTemplate(null);
    setIsEditorOpen(true);
  };

  const editTemplate = (template: CertificateTemplate) => {
    setEditingTemplate(template);
    setIsEditorOpen(true);
  };

  const getAssignedModuleName = (template: CertificateTemplate) => {
    if (!template.module_id) return null;
    const module = modules.find(m => m.id === template.module_id);
    return module ? (language === 'en' ? module.title_en : module.title_el) : null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">
          {language === 'en' ? 'Certificate Templates' : 'Πρότυπα Πιστοποιητικών'}
        </h3>
        <div className="flex gap-2">
          <Dialog open={isAssignmentDialogOpen} onOpenChange={setIsAssignmentDialogOpen}>
            <Button variant="outline" onClick={() => setIsAssignmentDialogOpen(true)}>
              <Settings className="h-4 w-4 mr-2" />
              {language === 'en' ? 'Assign Templates' : 'Ανάθεση Προτύπων'}
            </Button>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {language === 'en' ? 'Assign Template to Module' : 'Ανάθεση Προτύπου σε Μοντέλο'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">
                    {language === 'en' ? 'Training Module' : 'Εκπαιδευτικό Μοντέλο'}
                  </label>
                  <Select value={selectedModuleId} onValueChange={setSelectedModuleId}>
                    <SelectTrigger>
                      <SelectValue placeholder={language === 'en' ? 'Select module' : 'Επιλέξτε μοντέλο'} />
                    </SelectTrigger>
                    <SelectContent>
                      {modules.map((module) => (
                        <SelectItem key={module.id} value={module.id}>
                          {language === 'en' ? module.title_en : module.title_el}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">
                    {language === 'en' ? 'Certificate Template' : 'Πρότυπο Πιστοποιητικού'}
                  </label>
                  <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                    <SelectTrigger>
                      <SelectValue placeholder={language === 'en' ? 'Select template' : 'Επιλέξτε πρότυπο'} />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsAssignmentDialogOpen(false)}>
                    {language === 'en' ? 'Cancel' : 'Ακύρωση'}
                  </Button>
                  <Button 
                    onClick={assignTemplateToModule}
                    disabled={!selectedModuleId || !selectedTemplateId}
                  >
                    {language === 'en' ? 'Assign' : 'Ανάθεση'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <Button onClick={addNewTemplate}>
            <Plus className="h-4 w-4 mr-2" />
            {language === 'en' ? 'Add Template' : 'Προσθήκη Προτύπου'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map((template) => (
          <Card key={template.id} className="relative">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <FileText className="h-4 w-4" />
                  <CardTitle className="text-base">{template.name}</CardTitle>
                  {template.is_default && (
                    <Star className="h-4 w-4 text-yellow-500 fill-current" />
                  )}
                </div>
                <div className="flex space-x-1">
                  <Button variant="ghost" size="sm" onClick={() => editTemplate(template)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => duplicateTemplate(template)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => deleteTemplate(template.id)}
                    disabled={template.is_default}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="aspect-[4/5] bg-gray-100 rounded border overflow-hidden">
                <div 
                  className="w-full h-full flex flex-col items-center justify-center text-xs font-bold relative p-4"
                  style={{
                    backgroundImage: template.canvas_data?.background ? `url(${template.canvas_data.background})` : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundColor: '#ffffff'
                  }}
                >
                  {/* Overlay for better text visibility when there's a background image */}
                  {template.canvas_data?.background && (
                    <div className="absolute inset-0 bg-white/80" />
                  )}
                  
                  {/* Certificate Template Preview */}
                  <div className="text-center space-y-2 text-gray-800 w-full relative z-10">
                    {/* Logo */}
                    <div className="flex justify-center mb-2">
                      {template.canvas_data?.elements?.logo?.url ? (
                        <img 
                          src={template.canvas_data.elements.logo.url} 
                          alt="Logo" 
                          className="h-8 w-auto object-contain"
                        />
                      ) : (
                        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center relative">
                          <svg width="16" height="16" viewBox="0 0 16 16" className="text-white">
                            <g stroke="currentColor" strokeWidth="1.5" fill="none">
                              <path d="M3 6 L5 8 L3 10" />
                              <path d="M13 6 L11 8 L13 10" />
                              <path d="M6 6 L10 10" />
                              <path d="M10 6 L6 10" />
                            </g>
                          </svg>
                        </div>
                      )}
                    </div>
                   
                    <div className="text-lg font-bold mt-3 text-black">
                      {template.canvas_data?.elements?.title || 'CERTIFICATE'}
                    </div>
                    <div className="text-xs text-orange-500 font-semibold">
                      {template.canvas_data?.elements?.subtitle || 'OF COMPLETION'}
                    </div>
                    <div className="text-xs mt-2 font-semibold text-black">Employee Name</div>
                    <div className="text-xs text-gray-500">Training Title</div>
                    
                    {/* Badge levels */}
                    <div className="flex justify-center space-x-1 mt-2">
                      <div className="w-4 h-4 bg-yellow-500 rounded-full flex items-center justify-center">
                        <span className="text-[6px] text-white font-bold">G</span>
                      </div>
                      <div className="w-4 h-4 bg-gray-400 rounded-full flex items-center justify-center">
                        <span className="text-[6px] text-white font-bold">S</span>
                      </div>
                      <div className="w-4 h-4 bg-amber-600 rounded-full flex items-center justify-center">
                        <span className="text-[6px] text-white font-bold">B</span>
                      </div>
                    </div>
                    
                    {/* Footer text */}
                    <div className="mt-4 text-[8px] text-gray-500 italic">
                      {template.canvas_data?.elements?.footer || 'OVIT HR Department'}
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                {template.description}
              </p>
              <div className="flex items-center justify-between">
                {getAssignedModuleName(template) ? (
                  <Badge variant="default">
                    {getAssignedModuleName(template)}
                  </Badge>
                ) : (
                  <Badge variant="outline">
                    {language === 'en' ? 'No module assigned' : 'Κανένα μοντέλο'}
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground">
                  {new Date(template.created_at).toLocaleDateString()}
                </span>
              </div>
            </CardContent>
          </Card>
        ))}

        {templates.length === 0 && (
          <Card className="col-span-full">
            <CardContent className="flex flex-col items-center justify-center p-8">
              <FileText className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold text-muted-foreground mb-2">
                {language === 'en' ? 'No certificate templates yet' : 'Δεν υπάρχουν πρότυπα πιστοποιητικών'}
              </h3>
              <p className="text-muted-foreground text-center mb-4">
                {language === 'en' 
                  ? 'Create your first certificate template to customize how certificates look for your training modules.'
                  : 'Δημιουργήστε το πρώτο σας πρότυπο πιστοποιητικού για να προσαρμόσετε την εμφάνιση των πιστοποιητικών για τα εκπαιδευτικά σας μοντέλα.'
                }
              </p>
              <Button onClick={addNewTemplate}>
                <Plus className="h-4 w-4 mr-2" />
                {language === 'en' ? 'Create First Template' : 'Δημιουργία Πρώτου Προτύπου'}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      <CertificateEditor
        template={editingTemplate}
        isOpen={isEditorOpen}
        onClose={() => {
          setIsEditorOpen(false);
          setEditingTemplate(null);
        }}
        onSave={saveTemplate}
      />
    </div>
  );
};